import { CastMember, ReviewFinding } from '../../../../api/structs';
import { castMemberImageUrl } from './castMedia';

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Whether `text` names `term` as a WORD. The boundaries are "not a letter or a
 * digit" rather than `\b` so an apostrophe or a comma still counts ("Mara's
 * coat") while a longer word does not — "Maradona" is not Mara, and a picture
 * beside that finding would be a lie.
 */
const namesTerm = (text: string, term: string): boolean => {
  const needle = term.trim();
  if (!needle) return false;
  const pattern = new RegExp(
    `(^|[^\\p{L}\\p{N}])${escapeForRegExp(needle)}($|[^\\p{L}\\p{N}])`,
    'iu'
  );
  return pattern.test(text);
};

/**
 * The literal marker the judge's identity check carries, in both its wordings:
 * "…matches their CAST image; nobody else wears their identity". Matched
 * case-SENSITIVELY and as written, because the loose reading (any check with
 * the word "cast" in it) also fires on "colour cast" and "cast shadow" — and
 * splashing every character's face under a lighting finding is worse than
 * showing none.
 */
const CAST_IDENTITY_MARKER = 'CAST image';

/** The judge's own check on whether the characters are still themselves. */
const isCastIdentityCheck = (text: string): boolean => text.includes(CAST_IDENTITY_MARKER);

/**
 * Which cast members a FAILED check is about, so the panel can put their
 * reference picture beside it — the one thing that settles "is this the same
 * person?" faster than any wording.
 *
 * A check that names someone (in its criterion or its evidence, by name or by
 * an alias the story uses) is about them. The cast identity check names nobody
 * in particular — it is recognised by the `CAST image` marker its wording
 * carries — so it gets the whole cast. Members with no picture are left out
 * either way: there is nothing to show.
 *
 * A check that PASSED gets nothing — a wall of faces beside every green row
 * would bury the one row that needs them.
 */
export const castForFinding = (
  finding: ReviewFinding,
  cast: CastMember[] | undefined
): CastMember[] => {
  if ((finding.verdict || '').toUpperCase() !== 'FAIL') return [];
  const withPicture = (cast || []).filter((member) => castMemberImageUrl(member) !== '');
  if (withPicture.length === 0) return [];

  const text = `${finding.criterion || ''} ${finding.evidence || ''}`;
  const named = withPicture.filter(
    (member) =>
      namesTerm(text, member.name) ||
      (member.aliases || []).some((alias) => namesTerm(text, alias))
  );
  if (named.length > 0) return named;
  return isCastIdentityCheck(text) ? withPicture : [];
};

export default castForFinding;
